import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icons } from "../../../icons/icons";
import { useExperience } from "../../../context/ExperienceContext";
import { getPageHeroWhatsAppLink } from "../../../config/contact.config";
import { scrollToAfterElement } from "../../../utils/smoothScroll";
import "./PageHero.css";

const PAGE_SECTION_SCROLL_DELAY = 180;
const PAGE_SECTION_SCROLL_OFFSET = 96;

function sanitizeClassName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getRouteKey(pathname = "/") {
  if (pathname === "/") return "home";

  return (
    sanitizeClassName(pathname.replace(/^\/+/, "").split("/")[0]) || "page"
  );
}

function shouldHideCta(cta) {
  if (!cta) return true;
  if (cta.enabled === false) return true;
  if (cta.hide === true) return true;

  const label = String(cta.label || "").trim().toLowerCase();

  if (label === "x") return true;
  if (label === "false") return true;
  if (label === "none") return true;
  if (label === "null") return true;

  return false;
}

function getCtaIcon(icon) {
  const iconKey = String(icon || "").toLowerCase();

  const iconsMap = {
    whatsapp: Icons.FaWhatsapp,
    sparkles: Icons.Sparkles,
    calendar: Icons.Calendar,
    message: Icons.MessageCircle,
    arrow: Icons.ArrowRight,
    heart: Icons.Heart,
    leaf: Icons.Leaf,
    hearthandshake: Icons.HeartHandshake,
  };

  return iconsMap[iconKey] || null;
}

function getLegacyCta(content, type) {
  if (!content) return null;

  if (type === "primary" && content.primaryButton) {
    return {
      label: content.primaryButton,
      whatsappKey: content.primaryWhatsappKey || "scheduling",
      icon: "whatsapp",
    };
  }

  if (type === "secondary" && content.secondaryButton) {
    return {
      label: content.secondaryButton,
      to: content.secondaryTo || "#procedures",
      sectionTarget: content.secondarySectionTarget || ".procedures-section",
      icon: content.secondaryIcon || "sparkles",
    };
  }

  return null;
}

function normalizeCtas({ content, primaryCta, secondaryCta }) {
  const primary =
    primaryCta ?? content?.primaryCta ?? getLegacyCta(content, "primary");

  const secondary =
    secondaryCta ?? content?.secondaryCta ?? getLegacyCta(content, "secondary");

  return [
    {
      cta: primary,
      variant: "primary",
    },
    {
      cta: secondary,
      variant: "secondary",
    },
  ].filter((item) => !shouldHideCta(item.cta));
}

function getHeaderOffset() {
  const header = document.querySelector(".header");
  return header?.offsetHeight || PAGE_SECTION_SCROLL_OFFSET;
}

function scrollToPageSection(selector, options = {}) {
  const section = document.querySelector(selector);

  if (!section) return;

  const delay = options.delay ?? PAGE_SECTION_SCROLL_DELAY;
  const extraOffset = options.offset ?? 12;

  window.setTimeout(() => {
    const headerOffset = getHeaderOffset();

    const sectionTop =
      section.getBoundingClientRect().top +
      window.scrollY -
      headerOffset +
      extraOffset;

    window.scrollTo({
      top: sectionTop,
      behavior: "smooth",
    });
  }, delay);
}

function isServicesTarget(cta) {
  const to = String(cta?.to || "").trim().toLowerCase();
  const href = String(cta?.href || "").trim().toLowerCase();

  return (
    cta?.sectionTarget ||
    to === "/services" ||
    to === "services" ||
    to === "#procedures" ||
    to === "#services" ||
    href === "/services" ||
    href === "#procedures" ||
    href === "#services"
  );
}

function HeroButton({
  cta,
  variant,
  index = 0,
  mode = "all",
  onSectionNavigate,
}) {
  if (shouldHideCta(cta)) return null;

  const resolvedIcon =
    variant === "secondary" && mode === "all"
      ? "heartHandshake"
      : cta.icon;

  const Icon = getCtaIcon(resolvedIcon);

  const className = [
    "page-hero-premium__btn",
    `page-hero-premium__btn--${variant}`,
    `page-hero-premium__btn--delay-${index + 1}`,
    cta.className,
  ]
    .filter(Boolean)
    .join(" ");

  const children = (
    <>
      {Icon && <Icon />}
      <span>{cta.label || "Saiba mais"}</span>
    </>
  );

  if (cta.onClick) {
    return (
      <button type="button" className={className} onClick={cta.onClick}>
        {children}
      </button>
    );
  }

  if (isServicesTarget(cta)) {
    return (
      <button
        type="button"
        className={className}
        onClick={() =>
          onSectionNavigate?.(cta.sectionTarget || ".procedures-section")
        }
      >
        {children}
      </button>
    );
  }

  if (cta.to) {
    return (
      <a href={cta.to} className={className}>
        {children}
      </a>
    );
  }

  if (cta.href) {
    return (
      <a
        href={cta.href}
        target={cta.external === false ? undefined : "_blank"}
        rel={cta.external === false ? undefined : "noreferrer"}
        className={className}
      >
        {children}
      </a>
    );
  }

  if (cta.whatsappKey || cta.whatsappMessage || cta.whatsappContext) {
    const whatsappHref = cta.whatsappMessage
      ? getPageHeroWhatsAppLink(mode, cta.whatsappMessage)
      : getPageHeroWhatsAppLink(
          mode,
          cta.whatsappContext || cta.whatsappKey || "scheduling"
        );

    return (
      <a
        href={whatsappHref}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" className={className}>
      {children}
    </button>
  );
}

export default function PageHero({
  content = {},
  title,
  primaryCta,
  secondaryCta,
  image,
  imageAlt,
  className = "",
  showScrollIndicator,
}) {
  const { mode } = useExperience();
  const navigate = useNavigate();
  const location = useLocation();

  const heroRef = useRef(null);
  const [isScrollIndicatorVisible, setIsScrollIndicatorVisible] =
    useState(true);

  const routeKey = getRouteKey(location.pathname);
  const safeMode = sanitizeClassName(mode) || "all";

  const heroVariant = sanitizeClassName(
    content.heroVariant ||
      content.heroKey ||
      content.variant ||
      content.id ||
      ""
  );

  const imageVariant = sanitizeClassName(
    content.imageVariant ||
      content.imageKey ||
      content.imageMode ||
      heroVariant ||
      ""
  );

  const heroTitle = title || content.title;
  const heroImage = image || content.image;

  const heroImageAlt =
    imageAlt || content.imageAlt || content.title || "Dionara Krause";

  const shouldShowScrollIndicator =
    showScrollIndicator ?? content.showScrollIndicator ?? true;

  const actions = normalizeCtas({
    content,
    primaryCta,
    secondaryCta,
  });

  useEffect(() => {
    if (!shouldShowScrollIndicator) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      window.requestAnimationFrame(() => {
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const userIsNearTop = scrollY <= 80;

        setIsScrollIndicatorVisible(userIsNearTop);

        ticking = false;
      });

      ticking = true;
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [shouldShowScrollIndicator]);

  const handleSectionNavigate = (sectionTarget = ".procedures-section") => {
    if (location.pathname !== "/") {
      navigate("/");

      window.setTimeout(() => {
        scrollToPageSection(sectionTarget, {
          delay: 120,
          offset: 12,
        });
      }, 260);

      return;
    }

    scrollToPageSection(sectionTarget, {
      delay: 180,
      offset: 12,
    });
  };

  const handleScrollIndicatorClick = () => {
    scrollToAfterElement(heroRef.current, {
      delay: 180,
      offset: 24,
    });
  };

  const sectionClassName = [
    "page-hero-premium",
    `page-hero-premium--route-${routeKey}`,
    `page-hero-premium--mode-${safeMode}`,
    heroVariant ? `page-hero-premium--variant-${heroVariant}` : "",
    className,
    content.className,
  ]
    .filter(Boolean)
    .join(" ");

  const visualClassName = [
    "page-hero-premium__visual",
    "page-hero-premium__reveal",
    "page-hero-premium__reveal--visual",
    `page-hero-premium__visual--route-${routeKey}`,
    `page-hero-premium__visual--mode-${safeMode}`,
    imageVariant ? `page-hero-premium__visual--${imageVariant}` : "",
    content.visualClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const circleClassName = [
    "page-hero-premium__circle",
    `page-hero-premium__circle--route-${routeKey}`,
    `page-hero-premium__circle--mode-${safeMode}`,
    imageVariant ? `page-hero-premium__circle--${imageVariant}` : "",
    content.circleClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const imageClassName = [
    "page-hero-premium__image",
    `page-hero-premium__image--route-${routeKey}`,
    `page-hero-premium__image--mode-${safeMode}`,
    imageVariant ? `page-hero-premium__image--${imageVariant}` : "",
    content.imageClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section ref={heroRef} className={sectionClassName}>
      <div className="page-hero-premium__container">
        <div className="page-hero-premium__frame page-hero-premium__reveal page-hero-premium__reveal--frame">
          {content.eyebrow && (
            <div className="page-hero-premium__topline page-hero-premium__reveal page-hero-premium__reveal--topline">
              <span />
              <strong>{content.eyebrow}</strong>
              <span />
            </div>
          )}

          <div className="page-hero-premium__grid">
            <div className="page-hero-premium__content">
              {heroTitle && (
                <h1 className="page-hero-premium__reveal page-hero-premium__reveal--title">
                  {heroTitle}
                </h1>
              )}

              {content.description && (
                <p className="page-hero-premium__reveal page-hero-premium__reveal--text">
                  {content.description}
                </p>
              )}

              {actions.length > 0 && (
                <div className="page-hero-premium__actions page-hero-premium__reveal page-hero-premium__reveal--actions">
                  {actions.map(({ cta, variant }, index) => (
                    <HeroButton
                      key={`${variant}-${cta.label}`}
                      cta={cta}
                      variant={variant}
                      index={index}
                      mode={mode}
                      onSectionNavigate={handleSectionNavigate}
                    />
                  ))}
                </div>
              )}
            </div>

            {heroImage && (
              <div className={visualClassName}>
                <div className={circleClassName} />

                <img
                  src={heroImage}
                  alt={heroImageAlt}
                  className={imageClassName}
                />
              </div>
            )}
          </div>
        </div>

        {shouldShowScrollIndicator && (
          <button
            type="button"
            className={`page-hero-premium__scroll-indicator ${
              isScrollIndicatorVisible ? "is-visible" : "is-hidden"
            }`}
            onClick={handleScrollIndicatorClick}
            aria-label="Rolar para a próxima seção"
          >
            <span className="page-hero-premium__mouse" aria-hidden="true">
              <span />
            </span>

            <Icons.ChevronRight
              className="page-hero-premium__arrow"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </section>
  );
}